import { useEffect, useRef } from "react";

const spreadOf = (touches) =>
  Math.hypot(
    touches[0].clientX - touches[1].clientX,
    touches[0].clientY - touches[1].clientY,
  );

/**
 * Two-finger zoom for a scrolling panel.
 *
 * The site sets user-scalable=no, so the browser never zooms and the gesture
 * has to be read here. Listeners go on the scrolling element rather than the
 * content, because the content is only as big as the page it renders and a
 * pinch often starts on the padding around it. Safari reports a pinch as
 * gesture events instead of a second touch, so both are handled. With `wheel`
 * a trackpad pinch on a computer is read too: browsers report it as a wheel
 * event with the control key held.
 */
export default function usePinchZoom(ref, { min, max, onZoom, value, enabled = true, wheel = false }) {
  const stateRef = useRef(null);
  const onZoomRef = useRef(onZoom);
  const valueRef = useRef(value);
  onZoomRef.current = onZoom;
  valueRef.current = value;

  useEffect(() => {
    const element = ref.current;
    if (!element || !enabled) return undefined;

    const clamp = (value) => Math.min(max, Math.max(min, Math.round(value)));

    const begin = (zoom) => {
      stateRef.current = { zoom };
    };

    const applyRatio = (ratio) => {
      if (!stateRef.current) return;
      onZoomRef.current(clamp(stateRef.current.zoom * ratio));
    };

    const touchStart = (event) => {
      if (event.touches.length !== 2) return;
      stateRef.current = {
        zoom: valueRef.current,
        spread: spreadOf(event.touches),
      };
    };

    const touchMove = (event) => {
      if (event.touches.length !== 2 || !stateRef.current?.spread) return;
      event.preventDefault();
      applyRatio(spreadOf(event.touches) / stateRef.current.spread);
    };

    const touchEnd = (event) => {
      if (!event.touches || event.touches.length < 2) stateRef.current = null;
    };

    const gestureStart = (event) => {
      event.preventDefault();
      begin(valueRef.current);
    };

    const gestureChange = (event) => {
      event.preventDefault();
      applyRatio(event.scale);
    };

    const gestureEnd = () => {
      stateRef.current = null;
    };

    const wheelZoom = (event) => {
      if (!event.ctrlKey) return;
      // Otherwise the browser zooms the whole site.
      event.preventDefault();
      onZoomRef.current(clamp(valueRef.current * Math.exp(-event.deltaY / 100)));
    };

    if (wheel) element.addEventListener("wheel", wheelZoom, { passive: false });
    element.addEventListener("touchstart", touchStart, { passive: false });
    element.addEventListener("touchmove", touchMove, { passive: false });
    element.addEventListener("touchend", touchEnd);
    element.addEventListener("touchcancel", touchEnd);
    element.addEventListener("gesturestart", gestureStart, { passive: false });
    element.addEventListener("gesturechange", gestureChange, { passive: false });
    element.addEventListener("gestureend", gestureEnd);

    return () => {
      element.removeEventListener("wheel", wheelZoom);
      element.removeEventListener("touchstart", touchStart);
      element.removeEventListener("touchmove", touchMove);
      element.removeEventListener("touchend", touchEnd);
      element.removeEventListener("touchcancel", touchEnd);
      element.removeEventListener("gesturestart", gestureStart);
      element.removeEventListener("gesturechange", gestureChange);
      element.removeEventListener("gestureend", gestureEnd);
    };
  }, [enabled, max, min, ref, wheel]);
}
